import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import withCSRF from '../../../../commonComponents/withCSRF';
import translate from '../../../../commonComponents/translate';
import { compose } from '../../../../services/utilityFunctions';
import Notifications from '../../../../services/notifications';
import Request from '../../../../services/request';
import Form from '../../../../commonComponents/forms/Form';
import FormInput from '../../../../commonComponents/forms/FormInput';
import ConfigsContext from '../../../../contextProviders/ConfigsContext';

const isMethodEnabled = (methods, type) =>
  Array.isArray(methods) &&
  methods.some(m => m && m.type === type && m.enabled === true);

function LoginForm({
  t,
  _csrf,
  redirectLink,
  displayForgotPassword,
  displayOtpForm,
  onLoggedIn,
  ignoreRedirection,
}) {
  const formId = 'loginForm';
  const { organization, tenant } = useContext(ConfigsContext);
  const otpEnabledInTenant = isMethodEnabled(
    tenant && tenant.authMethods,
    'otpEmail',
  );
  const showOtpToggle = !!displayOtpForm && otpEnabledInTenant;
  const openTheSelectSponsorModal =
    organization?.settings?.roundMeDistributors?.enabled;

  const [postingForm, setPostingForm] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [organizationId, setOrganizationId] = useState('');
  const [organizationData, setOrganizationData] = useState(null);

  const validationRules = {
    email: { required: true, email: true },
    password: { required: true },
  };

  const submitButtonClasses = cx(
    'login-button',
    'btn',
    'm-btn',
    'm-btn--pill',
    'm-btn--custom',
    'm-btn--air',
    'm-login__btn',
    { 'm-loader': postingForm },
    { 'm-loader--right': postingForm },
    { 'm-loader--light': postingForm },
  );

  const onValidated = () => {
    setPostingForm(true);
    const params = {
      email,
      password,
      rememberMe,
    };
    if (organizationId) {
      params.organizationIdentifier = organizationId;
    }
    new Request(_csrf)
      .post('/signin/xhr', params)
      .done(() => {
        if (onLoggedIn) {
          onLoggedIn();
        }
        if (!ignoreRedirection) {
          if (redirectLink === '/shop/checkout') {
            window.location.href = `${redirectLink}?openSponsor=true`;
          } else {
            window.location.href = redirectLink;
          }
        }
      })
      .fail(err => {
        setPostingForm(false);
        Notifications.showErrorFromRequest(err);
        if (
          err &&
          err.responseJSON &&
          err.responseJSON.data &&
          err.responseJSON.data.organizationData
        ) {
          const orgData = err.responseJSON.data.organizationData;
          setOrganizationData(orgData);
          if (orgData.length > 0) {
            setOrganizationId(orgData[0].identifierCode);
          }
        }
      });
  };

  return (
    <div className="m-login__signin">
      <div className="m-login__head">
        <h3 className="m-login__title">{t('login_login')}</h3>
      </div>
      <Form
        className="m-login__form m-form"
        id={formId}
        submitMethod={onValidated}
        validationRules={validationRules}
      >
        {submitHandler => (
          <>
            {organizationData && (
              <div id="login-organization-select">
                <FormInput
                  type="select"
                  onChange={setOrganizationId}
                  id="organizationIdentifier"
                  placeholder={t('organization_holder')}
                  value={organizationId}
                  inputClassName="form-control m-input login-input"
                  containerClassName="form-control m-input login-input"
                  groupProps={{
                    className: 'form-control m-input login-input',
                  }}
                  inputProps={{
                    items: organizationData.map(o => ({
                      id: o.identifierCode,
                      text: o.name,
                    })),
                    dropdownParent: '#modalBody',
                  }}
                />
              </div>
            )}
            <FormInput
              type="email"
              name="email"
              inputProps={{ autoComplete: 'off' }}
              placeholder={t('email_holder')}
              value={email}
              onChange={setEmail}
              inputClassName="login-input"
            />
            <FormInput
              type="password"
              placeholder={t('pass_holder')}
              name="password"
              value={password}
              onChange={setPassword}
              inputClassName="login-input"
            />
            <div className="row m-login__form-sub">
              <div className="col m--align-left">
                {/* <Checkbox */}
                {/*  id="rememberMe" */}
                {/*  name="rememberMe" */}
                {/*  label={t('login_rememberMe')} */}
                {/*  value={rememberMe} */}
                {/*  onChange={() => setRememberMe(prev => !prev)} */}
                {/*  otherColor */}
                {/*  className="m-checkbox-remember" */}
                {/* /> */}
              </div>
              <div className="col m--align-right">
                <button
                  type="button"
                  id="m_login_forget_password"
                  className="m-button-forgot"
                  onClick={displayForgotPassword}
                >
                  {t('login_forgotPassword')}
                </button>
              </div>
            </div>
            <div className="m-login__form-action">
              <button
                type="submit"
                className={submitButtonClasses}
                disabled={postingForm}
                onClick={submitHandler}
              >
                {t('login_login')}
              </button>
            </div>
            {showOtpToggle && (
              <div className="m-login__form-action">
                <button
                  type="button"
                  onClick={displayOtpForm}
                  className="m-button-forgot"
                >
                  {t('login_otp_use_email_code')}
                </button>
              </div>
            )}
          </>
        )}
      </Form>
    </div>
  );
}

LoginForm.propTypes = {
  t: PropTypes.func.isRequired,
  _csrf: PropTypes.string.isRequired,
  redirectLink: PropTypes.string.isRequired,
  displayForgotPassword: PropTypes.func.isRequired,
  displayOtpForm: PropTypes.func,
  onLoggedIn: PropTypes.func,
  ignoreRedirection: PropTypes.bool,
};

LoginForm.defaultProps = {
  displayOtpForm: null,
  onLoggedIn: null,
  ignoreRedirection: false,
};

export default compose(translate, withCSRF)(LoginForm);
